/**
 * /bank-accounts/:bankAccountId — detail page for a single bank account.
 *
 * Header: account name, bank name, masked account number, currency, balance.
 * Tabs: Transactions | Deposits | Withdrawals | Transfers
 *
 * - Transactions: running ledger from GET /api/bank-accounts/{id}/transactions
 * - Deposits:     list of deposit journals + "New Deposit" dialog
 * - Withdrawals:  list of withdrawal journals + "New Withdrawal" dialog
 * - Transfers:    "New Transfer" dialog (bank-to-bank within entity)
 *
 * The list pages for deposits/withdrawals are derived from the transaction
 * ledger (source filter) — the API doesn't have separate list endpoints.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, Tab, Tabs, TextField,
  Tooltip,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

import { api, getToken } from "@/lib/api";
import {
  accountsListQueryOptions,
  bankAccountDetailQueryOptions,
  bankAccountKeys,
  bankAccountTransactionsQueryOptions,
  bankAccountsListQueryOptions,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { MoneyField } from "@/components/MoneyField";
import type { BankTransaction, UploadDocumentResponse } from "@/lib/types";
import { BankRegisterGrid } from "@/grids/BankRegisterGrid";
import { codeAndName } from "../lib/displayCode";

export const Route = createFileRoute("/_auth/bank-accounts/$bankAccountId")({
  component: BankAccountDetailPage,
});

type TabKey = "transactions" | "deposits" | "withdrawals" | "transfers";

function fmtCurrency(amount: number | string | null | undefined, currency = "USD") {
  const n = Number(amount ?? 0);
  return n.toLocaleString(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function BankAccountDetailPage() {
  const { bankAccountId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const accountQuery = useQuery(bankAccountDetailQueryOptions(bankAccountId));
  const txQuery = useQuery(bankAccountTransactionsQueryOptions(bankAccountId, 500));

  const [tab, setTab] = useState<TabKey>("transactions");
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const bank = accountQuery.data;
  const txRows = txQuery.data ?? [];

  // Partition rows by source for Deposits / Withdrawals tabs
  const deposits = useMemo(
    () => txRows.filter((t) => Number(t.debit) > 0),
    [txRows],
  );
  const withdrawals = useMemo(
    () => txRows.filter((t) => Number(t.credit) > 0),
    [txRows],
  );

  const onMutated = () => {
    qc.invalidateQueries({ queryKey: bankAccountKeys.all });
    toast.success("Posted successfully");
  };

  if (accountQuery.isLoading) {
    return (
      <div className="flex h-[calc(100vh-56px)] items-center justify-center text-sm text-slate-500 dark:text-slate-400">
        Loading…
      </div>
    );
  }

  if (!bank) {
    return (
      <div className="p-6">
        <div className="text-sm text-red-600">Bank account not found.</div>
        <button
          className="mt-4 text-sm text-blue-600 hover:underline"
          onClick={() => navigate({ to: "/bank-accounts" })}
        >
          ← Back to bank accounts
        </button>
      </div>
    );
  }

  const isCredit = bank.account_type === "credit_line" || bank.account_type === "loan";
  const balance = Number(bank.current_balance ?? 0);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="text-xs text-slate-500 mb-1 dark:text-slate-400">
          <Link to="/bank-accounts" className="hover:text-slate-700 dark:hover:text-slate-200">
            Bank Accounts
          </Link>
          <span className="mx-2">/</span>
          <span className="font-medium text-slate-700 dark:text-slate-300">{bank.name}</span>
        </div>
        <div className="flex items-start gap-6 mt-1">
          {/* Account info */}
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{bank.name}</h1>
            <div className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">
              {bank.bank_name}
              {bank.account_number_last4 && (
                <span className="font-mono ml-2 text-slate-500">•••{bank.account_number_last4}</span>
              )}
              <span className="mx-2 text-slate-300 dark:text-slate-600">·</span>
              <span className="uppercase text-xs tracking-wide">{bank.account_type.replace("_", " ")}</span>
              <span className="mx-2 text-slate-300 dark:text-slate-600">·</span>
              <span className="font-mono text-xs">{bank.currency_code}</span>
            </div>
            {(bank.gl_account_code || bank.gl_account_name) && (
              <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                GL: {codeAndName({ code: bank.gl_account_code, name: bank.gl_account_name }, " — ")}
              </div>
            )}
          </div>
          <div className="flex-1" />
          {/* Balance chip */}
          <div className="text-right">
            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {isCredit ? "Outstanding" : "Current Balance"}
            </div>
            <div className={`text-2xl font-mono font-semibold ${
              balance < 0 ? "text-red-700"
                : isCredit ? "text-amber-700"
                : "text-emerald-700"
            }`}>
              {fmtCurrency(bank.current_balance, bank.currency_code)}
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar + action buttons */}
      <div className="bg-white border-b border-slate-200 px-6 flex items-center gap-3 dark:bg-gray-900 dark:border-gray-800">
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v as TabKey)}
          textColor="primary"
          indicatorColor="primary"
          sx={{ minHeight: 42, "& .MuiTab-root": { minHeight: 42, fontSize: 13, py: 0 } }}
        >
          <Tab value="transactions" label="Transactions" />
          <Tab value="deposits" label={`Deposits (${deposits.length})`} />
          <Tab value="withdrawals" label={`Withdrawals (${withdrawals.length})`} />
          <Tab value="transfers" label="Transfers" />
        </Tabs>
        <div className="flex-1" />
        {tab === "deposits" && (
          <button
            onClick={() => setDepositOpen(true)}
            className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            + New Deposit
          </button>
        )}
        {tab === "withdrawals" && (
          <button
            onClick={() => setWithdrawOpen(true)}
            className="text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            − New Withdrawal
          </button>
        )}
        {tab === "transfers" && (
          <button
            onClick={() => setTransferOpen(true)}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            ⇄ New Transfer
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {txQuery.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading transactions…</div>
        )}

        {tab === "transactions" && !txQuery.isLoading && (
          <BankRegisterGrid rows={[...txRows].reverse()} loading={txQuery.isLoading} />
        )}

        {tab === "deposits" && !txQuery.isLoading && (
          <BankRegisterGrid rows={[...deposits].reverse()} loading={txQuery.isLoading} />
        )}

        {tab === "withdrawals" && !txQuery.isLoading && (
          <BankRegisterGrid rows={[...withdrawals].reverse()} loading={txQuery.isLoading} />
        )}

        {tab === "transfers" && !txQuery.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            Transfers appear in the Transactions tab as journal entries.
            Use the + New Transfer button to move funds between your bank accounts.
          </div>
        )}
      </div>

      {/* Dialogs */}
      {depositOpen && (
        <DepositWithdrawDialog
          bankAccountId={bankAccountId}
          bankName={bank.name}
          currency={bank.currency_code}
          kind="deposit"
          onClose={() => setDepositOpen(false)}
          onPosted={() => { setDepositOpen(false); onMutated(); }}
        />
      )}
      {withdrawOpen && (
        <DepositWithdrawDialog
          bankAccountId={bankAccountId}
          bankName={bank.name}
          currency={bank.currency_code}
          kind="withdrawal"
          onClose={() => setWithdrawOpen(false)}
          onPosted={() => { setWithdrawOpen(false); onMutated(); }}
        />
      )}
      {transferOpen && (
        <TransferDialog
          bankAccountId={bankAccountId}
          bankName={bank.name}
          currency={bank.currency_code}
          onClose={() => setTransferOpen(false)}
          onPosted={() => { setTransferOpen(false); onMutated(); }}
        />
      )}
    </div>
  );
}




// Funding source options for deposits — maps to GL account codes
const FUNDING_SOURCES = [
  { label: "Owner loan / advance",      code: "2200", hint: "Notes Payable — owner lent money to the business" },
  { label: "Bank loan draw",            code: "2201", hint: "Proceeds from a bank line of credit or term loan" },
  { label: "Customer payment (misc AR)", code: "1200", hint: "Cash received that isn't tied to a specific AR invoice" },
  { label: "Revenue / sales",           code: "4000", hint: "Direct cash sales not invoiced" },
  { label: "Owner equity contribution", code: "3000", hint: "Owner putting in capital (not a loan)" },
  { label: "Interest earned",           code: "4900", hint: "Interest income from savings / investments" },
  { label: "Other (enter GL code)",     code: "__custom__", hint: "Type the GL account code directly" },
];

interface ParsedSlip {
  amount: string | null;
  deposit_date: string | null;
  bank_name: string | null;
  account_last4: string | null;
  transaction_ref: string | null;
  suggested_description: string | null;
  confidence: number;
}

// ─────────────────────────────────────────────────────────────
// Deposit / Withdrawal dialog (with OCR slip scan for deposits)
// ─────────────────────────────────────────────────────────────
function DepositWithdrawDialog({
  bankAccountId,
  bankName,
  currency,
  kind,
  onClose,
  onPosted,
}: {
  bankAccountId: string;
  bankName: string;
  currency: string;
  kind: "deposit" | "withdrawal";
  onClose: () => void;
  onPosted: () => void;
}) {
  const toast = useToast();
  const isDeposit = kind === "deposit";

  const [amount, setAmount] = useState("");
  const [fundingKey, setFundingKey] = useState("2200");  // selected option code (or "__custom__")
  const [customCode, setCustomCode] = useState("");
  const [withdrawCode, setWithdrawCode] = useState("6999");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [memo, setMemo] = useState("");
  const [fxRate, setFxRate] = useState("");
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [docSha, setDocSha] = useState<string | null>(null);
  const [docName, setDocName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState<ParsedSlip | null>(null);

  const counterpartyCode = isDeposit
    ? (fundingKey === "__custom__" ? customCode : fundingKey)
    : withdrawCode;

  const handleFile = async (file: File) => {
    // Run upload + OCR in parallel for deposits; just upload for withdrawals
    const uploadPromise = (async () => {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file, file.name);
        const r = await fetch("/api/bank-accounts/upload-document", {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken() ?? ""}` },
          body: fd,
        });
        if (!r.ok) {
          let detail = `${r.status} ${r.statusText}`;
          try { const j = await r.json(); detail = j.detail ?? detail; } catch { /* */ }
          throw new Error(detail);
        }
        const result = (await r.json()) as UploadDocumentResponse;
        setDocUrl(result.url);
        setDocSha(result.sha256);
        setDocName(result.filename);
      } finally {
        setUploading(false);
      }
    })();

    if (isDeposit) {
      const ocrPromise = (async () => {
        setScanning(true);
        try {
          const fd = new FormData();
          fd.append("file", file, file.name);
          const r = await fetch(`/api/bank-accounts/${bankAccountId}/parse-deposit-slip`, {
            method: "POST",
            headers: { Authorization: `Bearer ${getToken() ?? ""}` },
            body: fd,
          });
          if (!r.ok) return; // silent — OCR failure shouldn't block the manual form
          const parsed = (await r.json()) as ParsedSlip;
          setScanned(parsed);
          // Auto-fill only if confidence is reasonable
          if (parsed.confidence >= 0.6) {
            if (parsed.amount) setAmount(parsed.amount);
            if (parsed.deposit_date) setDate(parsed.deposit_date);
            if (parsed.suggested_description) setDescription(parsed.suggested_description);
          }
        } catch {
          // silent — don't break the form
        } finally {
          setScanning(false);
        }
      })();
      await Promise.allSettled([uploadPromise, ocrPromise]);
    } else {
      await uploadPromise;
    }
  };

  const post = useMutation({
    mutationFn: async () => {
      const endpoint = isDeposit
        ? `/api/bank-accounts/${bankAccountId}/deposits`
        : `/api/bank-accounts/${bankAccountId}/withdrawals`;
      const body: Record<string, unknown> = {
        amount,
        counterparty_account_code: counterpartyCode,
        description,
        memo: memo || null,
        source_document_url: docUrl,
        source_document_sha256: docSha,
      };
      body[isDeposit ? "deposit_date" : "withdrawal_date"] = date;
      if (fxRate) body.fx_rate = fxRate;
      return api(endpoint, { method: "POST", body });
    },
    onSuccess: () => { toast.success(`${isDeposit ? "Deposit" : "Withdrawal"} posted`); onPosted(); },
    onError: (err: Error) => toast.error(`Post failed: ${err.message}`),
  });

  const selectedFunding = FUNDING_SOURCES.find((s) => s.code === fundingKey);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isDeposit ? "Record deposit" : "Record withdrawal"}{" "}
        <span className="text-slate-500 text-base font-normal dark:text-slate-400">
          — {bankName} ({currency})
        </span>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>

          {/* ── Drop zone (with OCR for deposits) ── */}
          <div
            className={`border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors
              ${scanning ? "border-blue-400 bg-blue-50 dark:bg-blue-900/10" : "border-slate-300 hover:border-blue-400 hover:bg-blue-50 dark:border-gray-700 dark:hover:bg-blue-900/10"}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) void handleFile(f);
            }}
            onClick={() => {
              const inp = document.createElement("input");
              inp.type = "file";
              inp.accept = "application/pdf,image/*";
              inp.onchange = () => { const f = inp.files?.[0]; if (f) void handleFile(f); };
              inp.click();
            }}
          >
            {(uploading || scanning) ? (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <CircularProgress size={14} />
                {scanning ? "Scanning slip…" : "Uploading…"}
              </div>
            ) : docUrl ? (
              <div>
                <div className="text-sm text-emerald-700 font-medium">✓ {docName}</div>
                <div className="text-xs text-slate-500 mt-0.5">Click or drop another to replace</div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-center gap-1.5 text-sm text-slate-600 font-medium dark:text-slate-300">
                  {isDeposit && <AutoFixHighIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                  {isDeposit ? "Drop deposit slip photo to scan" : "Drop or click to attach a document"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">
                  {isDeposit
                    ? "Amount and date auto-filled from photo · PDF, JPG, PNG, HEIC"
                    : "Wire scan, withdrawal advice · PDF, JPG, PNG, HEIC up to 25 MB"}
                </div>
              </div>
            )}
          </div>

          {/* OCR result banner */}
          {scanned && (
            <Alert
              severity={scanned.confidence >= 0.8 ? "success" : "info"}
              icon={<AutoFixHighIcon fontSize="inherit" />}
            >
              <strong>Slip scanned</strong>
              {scanned.bank_name && ` · ${scanned.bank_name}`}
              {scanned.account_last4 && ` ···${scanned.account_last4}`}
              {scanned.transaction_ref && ` · ${scanned.transaction_ref}`}
              <Tooltip title={`OCR confidence: ${Math.round(scanned.confidence * 100)}%`}>
                <Chip
                  label={`${Math.round(scanned.confidence * 100)}% confidence`}
                  size="small"
                  color={scanned.confidence >= 0.8 ? "success" : "warning"}
                  variant="outlined"
                  sx={{ ml: 1, height: 18, fontSize: 10 }}
                />
              </Tooltip>
              {scanned.confidence < 0.6 && (
                <> — low confidence, please verify the fields below</>
              )}
            </Alert>
          )}

          {/* Amount + Date */}
          <Stack direction="row" spacing={2}>
            <MoneyField
              size="small" label="Amount"
              currency={currency}
              value={amount}
              onChange={setAmount}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small" label="Date" type="date"
              value={date} onChange={(e) => setDate(e.target.value)}
              sx={{ flex: 1 }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          {/* Funding source (deposit) or GL code (withdrawal) */}
          {isDeposit ? (
            <>
              <TextField
                select size="small" label="Funding source"
                value={fundingKey}
                onChange={(e) => setFundingKey(e.target.value)}
                helperText={selectedFunding?.hint}
              >
                {FUNDING_SOURCES.map((s) => (
                  <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>
                ))}
              </TextField>
              {fundingKey === "__custom__" && (
                <TextField
                  size="small" label="GL account code (CREDIT)"
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value.trim())}
                  placeholder="e.g. 2210"
                />
              )}
            </>
          ) : (
            <TextField
              size="small"
              label="Destination GL account code (DEBIT)"
              value={withdrawCode}
              onChange={(e) => setWithdrawCode(e.target.value.trim())}
              helperText="Common: 2200 (loan repayment), 8010 (interest expense), 3060 (owner distribution), 6999 (misc)"
            />
          )}

          {/* Description + memo */}
          <TextField
            size="small" label="Description" value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={isDeposit ? "e.g. Owner loan deposit — Chase" : "e.g. Principal payment on term loan"}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              size="small" label="Memo (optional)" value={memo}
              onChange={(e) => setMemo(e.target.value)}
              sx={{ flex: 2 }}
            />
            <TextField
              size="small" label={`FX rate (${currency} → base)`}
              type="number" value={fxRate}
              onChange={(e) => setFxRate(e.target.value)}
              inputProps={{ step: "0.0001", min: 0 }}
              placeholder="1.0"
              sx={{ flex: 1 }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={isDeposit ? "success" : "warning"}
          disabled={!amount || !counterpartyCode || !description || post.isPending || uploading || scanning}
          onClick={() => post.mutate()}
        >
          {post.isPending ? "Posting…" : isDeposit ? "Post deposit" : "Post withdrawal"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────
// Transfer dialog (bank-to-bank within entity)
// ─────────────────────────────────────────────────────────────
function TransferDialog({
  bankAccountId,
  bankName,
  currency,
  onClose,
  onPosted,
}: {
  bankAccountId: string;
  bankName: string;
  currency: string;
  onClose: () => void;
  onPosted: () => void;
}) {
  const toast = useToast();
  const banksQuery = useQuery(bankAccountsListQueryOptions());
  const otherBanks = (banksQuery.data ?? []).filter(
    (b) => b.id !== bankAccountId && b.is_active,
  );

  const [destId, setDestId] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [memo, setMemo] = useState("");
  const [fxRate, setFxRate] = useState("");

  const selectedDest = otherBanks.find((b) => b.id === destId);

  const post = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        dest_bank_account_id: destId,
        amount,
        transfer_date: date,
        description,
        memo: memo || null,
      };
      if (fxRate) body.fx_rate = fxRate;
      return api(`/api/bank-accounts/${bankAccountId}/transfers`, {
        method: "POST",
        body,
      });
    },
    onSuccess: () => { toast.success("Transfer posted"); onPosted(); },
    onError: (err: Error) => toast.error(`Transfer failed: ${err.message}`),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Transfer funds
        <span className="text-slate-500 text-base font-normal ml-2 dark:text-slate-400">
          — from {bankName} ({currency})
        </span>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select size="small" label="To account"
            value={destId} onChange={(e) => setDestId(e.target.value)}
            helperText={
              selectedDest
                ? `${selectedDest.bank_name} · ${selectedDest.currency_code}`
                : "Pick the destination bank account"
            }
          >
            {otherBanks.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
                {b.account_number_last4 && ` (•••${b.account_number_last4})`}
                {" · "}
                {b.currency_code}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <MoneyField
              size="small" label="Amount"
              currency={currency}
              value={amount}
              onChange={setAmount}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small" label="Transfer date" type="date"
              value={date} onChange={(e) => setDate(e.target.value)}
              sx={{ flex: 1 }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <TextField
            size="small" label="Description" value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Transfer from operating to payroll account"
          />
          <Stack direction="row" spacing={2}>
            <TextField
              size="small" label="Memo (optional)" value={memo}
              onChange={(e) => setMemo(e.target.value)}
              sx={{ flex: 2 }}
            />
            {selectedDest && selectedDest.currency_code !== currency && (
              <TextField
                size="small"
                label={`FX rate (${currency} → ${selectedDest.currency_code})`}
                type="number" value={fxRate}
                onChange={(e) => setFxRate(e.target.value)}
                inputProps={{ step: "0.0001", min: 0 }}
                placeholder="1.0"
                sx={{ flex: 1 }}
              />
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!destId || !amount || !description || post.isPending}
          onClick={() => post.mutate()}
        >
          {post.isPending ? "Posting…" : "Post transfer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
